/**
 * The audit explorer's data: GET /api/audit/query for the current filter (debounced), more
 * pages on demand with a (before, beforeId) cursor so entries sharing a millisecond are never
 * skipped, and a quiet refetch of the first page whenever the hub reports a new audit entry over
 * SSE, merged in front of the pages already loaded. Retention, verify and export calls live here
 * too. Demo mode (?mock=1) runs the same query over the demo entries with the protocol's own
 * filter. A server without /api/audit/query (the Workers control plane) answers 404: the
 * explorer then filters the newest entries of the older GET /api/audit here.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { auditQueryString, runAuditQuery } from '@orgus/protocol';
import type { AuditEntry, AuditExportFormat, AuditQuery, AuditQueryResult, ChainVerifyResult, RetentionRun, RetentionSettings, RetentionState } from '@orgus/protocol';
import { ApiError, isUiErrorShape } from '@orgus/ui';
import type { AuditPage } from '@orgus/protocol';
import { api } from '../../lib/api.ts';
import { useOrgus, useAudit } from '../../lib/store.ts';
import { exportText, legacyQueryResult, mergeRows, mockArchivedEntries, mockRetention, mockVerify, normalizeVerify } from './model.ts';

const PAGE = 100;

export interface AuditQueryState {
  result: AuditQueryResult | null;
  /** Rows across every page loaded so far, newest first. */
  rows: AuditEntry[];
  loading: boolean;
  loadingMore: boolean;
  error: unknown;
  refresh(): void;
  loadMore(): void;
}

/** Servers that answered 404 to /api/audit/query: use the older list from then on. */
let legacyServer = false;

async function fetchQuery(q: AuditQuery): Promise<AuditQueryResult> {
  if (!legacyServer) {
    try {
      return await api<AuditQueryResult>(`/api/audit/query?${auditQueryString(q)}`);
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 404)) throw e;
      legacyServer = true;
    }
  }
  const page = await api<AuditPage>('/api/audit?limit=1000');
  return legacyQueryResult(page.entries, q);
}

function mockResult(all: AuditEntry[], q: AuditQuery): AuditQueryResult {
  const archived = q.archive ? mockArchivedEntries(Date.now()) : [];
  const r = runAuditQuery([...all, ...archived], { ...q, limit: q.limit ?? PAGE });
  return { ...r, scanned: all.length + archived.length, archive: { searched: !!q.archive, entries: archived.length, unreadable: [] } };
}

export function useAuditQuery(q: AuditQuery): AuditQueryState {
  const mock = useOrgus((s) => s.mode === 'mock');
  const recent = useAudit();
  const newestId = recent[0]?.id ?? '';
  const key = auditQueryString(q);
  const [result, setResult] = useState<AuditQueryResult | null>(null);
  const [rows, setRows] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const seq = useRef(0);
  /** The filter the rows on screen were loaded for: a quiet refresh merges only into those. */
  const rowsKey = useRef<string | null>(null);

  const fetchFirst = useCallback(
    async (quiet: boolean) => {
      const my = ++seq.current;
      if (!quiet) setLoading(true);
      try {
        const r = mock ? mockResult(useOrgus.getState().audit, { ...q, limit: PAGE }) : await fetchQuery({ ...q, limit: PAGE });
        if (my !== seq.current) return;
        if (quiet && rowsKey.current === key) {
          // Keep the pages already loaded (and an entry open from them): new entries go in front.
          setRows((old) => mergeRows(r.entries, old));
          // The oldest loaded row is still the cursor, so whether more exist beyond it has not changed.
          setResult((old) => (old ? { ...r, more: old.more } : r));
        } else {
          rowsKey.current = key;
          setResult(r);
          setRows(r.entries);
        }
        setError(null);
      } catch (e) {
        if (my === seq.current) setError(e);
      } finally {
        if (my === seq.current) setLoading(false);
      }
    },
    // The key captures q.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, mock],
  );

  // New filter: debounce typing, then load page one.
  useEffect(() => {
    const t = setTimeout(() => void fetchFirst(false), 200);
    return () => clearTimeout(t);
  }, [fetchFirst]);

  // A new entry arrived live: refresh page one without a spinner.
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const t = setTimeout(() => void fetchFirst(true), 400);
    return () => clearTimeout(t);
  }, [newestId, fetchFirst]);

  const loadMore = useCallback(() => {
    const oldest = rows[rows.length - 1];
    if (!oldest || loadingMore) return;
    setLoadingMore(true);
    const my = seq.current;
    const next: AuditQuery = { ...q, limit: PAGE, before: oldest.at, beforeId: oldest.id };
    (mock ? Promise.resolve(mockResult(useOrgus.getState().audit, next)) : fetchQuery(next))
      .then((r) => {
        if (my !== seq.current) return;
        setRows((old) => mergeRows(old, r.entries));
        setResult((old) => (old ? { ...old, more: r.more } : r));
      })
      .catch((e) => setError(e))
      .finally(() => setLoadingMore(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, loadingMore, key, mock]);

  return { result, rows, loading, loadingMore, error, refresh: () => void fetchFirst(false), loadMore };
}

// ---------------------------------------------------------------------------
// Verify, retention, export
// ---------------------------------------------------------------------------

export async function verifyChain(): Promise<ChainVerifyResult> {
  if (useOrgus.getState().mode === 'mock') {
    await new Promise((r) => setTimeout(r, 500));
    return mockVerify(Date.now(), useOrgus.getState().audit.length, mockArchivedEntries(Date.now()).length);
  }
  // The control plane and older hubs answer { ok, checked }: shown the same way.
  return normalizeVerify(await api<unknown>('/api/audit/verify', { timeoutMs: 120_000 }), Date.now());
}

/**
 * What the next retention run would archive with this live age: how many live entries are older
 * than it, and the newest of them. The Retention dialog uses it to warn before a run that would
 * archive and delete entries at once.
 */
export async function archiveBatch(liveDays: number | null): Promise<{ total: number; newest: number | null } | null> {
  if (liveDays == null) return null;
  const q: AuditQuery = { to: Date.now() - liveDays * 86_400_000, limit: 1 };
  const r = useOrgus.getState().mode === 'mock' ? runAuditQuery(useOrgus.getState().audit, q) : await fetchQuery(q);
  return { total: r.total, newest: r.entries[0]?.at ?? null };
}

let mockRetentionState: RetentionState | null = null;

export async function getRetention(): Promise<RetentionState> {
  if (useOrgus.getState().mode === 'mock') return (mockRetentionState ??= mockRetention(Date.now(), useOrgus.getState().audit));
  return api<RetentionState>('/api/audit/retention');
}

export async function saveRetention(s: RetentionSettings): Promise<RetentionState> {
  if (useOrgus.getState().mode === 'mock') {
    const cur = await getRetention();
    mockRetentionState = { ...cur, settings: s };
    return mockRetentionState;
  }
  return api<RetentionState>('/api/audit/retention', { method: 'PUT', body: s });
}

export async function runRetention(): Promise<{ run: RetentionRun; state: RetentionState }> {
  if (useOrgus.getState().mode === 'mock') {
    const cur = await getRetention();
    const run: RetentionRun = { at: Date.now(), by: 'owner', archived: 0, pruned: 0, ok: true, message: 'No entries are old enough to archive.' };
    mockRetentionState = { ...cur, lastRun: run };
    return { run, state: mockRetentionState };
  }
  return api<{ run: RetentionRun; state: RetentionState }>('/api/audit/retention/run', { method: 'POST', body: {}, timeoutMs: 120_000 });
}

/** Download the filtered log. Errors (a missing role, a hub without the module) are thrown as ApiError. */
export async function downloadExport(q: AuditQuery, format: AuditExportFormat): Promise<void> {
  const st = useOrgus.getState();
  let blob: Blob;
  let name = `orgus-audit.${format}`;
  if (st.mode === 'mock') {
    const all = runAuditQuery([...st.audit, ...(q.archive ? mockArchivedEntries(Date.now()) : [])], { ...q, limit: 1000 }).entries;
    blob = new Blob([exportText(all, format, st.machines)], { type: format === 'csv' ? 'text/csv' : 'application/x-ndjson' });
    name = `orgus-audit-demo.${format}`;
  } else {
    const { limit: _l, before: _b, ...rest } = q;
    const res = await fetch(`/api/audit/export?format=${format}&${auditQueryString(rest)}`, { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
      const ui = isUiErrorShape(body?.error)
        ? body.error
        : { what: 'The export failed.', why: `The hub answered with status ${res.status}.`, next: 'Try again. If it keeps failing, check the hub terminal.' };
      throw new ApiError({ ...ui, status: res.status }, res.status);
    }
    blob = await res.blob();
    name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? name;
  }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}
